// Converts a validated completion into safe owner state without exposing raw
// output, errors, or tool identity.
import { z } from 'zod';

import { parseOutreachReceipt } from '@apps/web/features/phoebe_chat_v3/model/outreach_receipt';
import { parseRecommendationsReceipt } from '@apps/web/features/phoebe_chat_v3/model/recommendations_receipt';
import { parseWriteReceipt } from '@apps/web/features/phoebe_chat_v3/model/write_receipt';
import {
    type AgencyRulesPresentation,
    defineCompoundMember,
    type GeneratedTablePresentation,
    SELECTABLE_ID_COLUMNS,
    type SelectableIdColumn,
    type ToolResultFragment,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import {
    parseAgencyRulesReceipt,
    parseRenderedRowsReceipt,
    type RenderedRowsReceipt,
} from '@apps/web/lib/agent_render_artifact';

// The executor writes "incomplete", never "failed", when a tool raises
// (agent_event_framework/tool_execution.py). Mirror the server's
// tool_output_status_for_item allowlist so an errored tool cannot render
// as a completed activity.
const TERMINAL_SUCCESS_STATUSES = new Set(['completed', 'succeeded']);

const questionAnswerOutputSchema = z
    .object({
        status: z.literal('answered'),
        answer: z.union([z.string(), z.array(z.string())]),
    })
    .strip();

const chatAttachmentOutputSchema = z.object({
    chat_attachment: z.object({
        attachment_id: z.uuid(),
        filepath: z.string().min(1),
        filename: z.string().min(1),
        content_type: z.string().min(1),
        disposition: z.enum(['inline', 'attachment']),
        size_bytes: z.number().int().nonnegative(),
    }),
});

const noteOutputSchema = z.object({
    results: z
        .array(
            z.object({
                saved: z.boolean(),
                detail: z.string().optional(),
            }),
        )
        .min(1),
});

const NEXT_STEPS_TOOL_NAME = 'suggest_next_steps';
const SEND_SMS_TOOL_NAME = 'send_sms';
const PLACE_VOICE_CALL_TOOL_NAME = 'place_voice_call';

// `notes` is the agent's channel, not the card's: never render it here.
const assignmentOutputSchema = z.object({
    ehr_writeback_skipped: z.boolean().optional(),
    status: z.enum(['assigned', 'partial', 'needs_confirmation', 'rejected']),
    results: z.array(
        z.object({
            shift_id: z.uuid(),
            status: z.enum([
                'assigned',
                'failed',
                'not_attempted',
                'needs_confirmation',
                'refused',
            ]),
        }),
    ),
});

// A completed tool invocation can still contact nobody. Keep the domain
// status narrow so the transcript never promotes execution into delivery.
const sendSmsOutputSchema = z
    .object({
        status: z.enum([
            'sent',
            'partial',
            'needs_confirmation',
            'blocked',
            'interrupted',
        ]),
    })
    .strip();
const placeVoiceCallOutputSchema = z
    .object({
        status: z.enum(['calling', 'needs_confirmation']),
    })
    .strip();

function parseJsonOutputText(outputText: string | undefined): unknown {
    if (outputText === undefined) {
        return undefined;
    }
    try {
        return JSON.parse(outputText);
    } catch {
        return undefined;
    }
}

function selectableIdColumn(
    columns: ReadonlyArray<string>,
): SelectableIdColumn | null {
    return (
        SELECTABLE_ID_COLUMNS.find((column) => columns.includes(column)) ?? null
    );
}

// Domain receipts carry no title; the component names what the rows are.
const DOMAIN_TABLE_TITLES: Readonly<Record<string, string>> = {
    shifts: 'Shifts',
    caregivers: 'Caregivers',
    client_detail: 'Client',
};

export function renderedTableTitle(
    component: RenderedRowsReceipt['component'],
    title?: string,
): string | null {
    return title ?? DOMAIN_TABLE_TITLES[component] ?? null;
}

export function buildRenderedTablePresentation(
    receipt: RenderedRowsReceipt,
    workspacePath: string,
    sourceEventId: string,
): GeneratedTablePresentation {
    const idColumn = selectableIdColumn(receipt.columns);
    const actions = receipt.selection_actions ?? [];
    return {
        kind: 'table',
        ...(receipt.component !== 'table' ||
        (receipt.context_entity !== null &&
            receipt.context_entity !== undefined)
            ? {
                  uiContextReceipt: {
                      file_path: workspacePath,
                      source: 'render' as const,
                      presentation: receipt.component,
                      row_count: receipt.row_count,
                      content_sha256: receipt.content_sha256,
                      event_id: sourceEventId,
                      ...(receipt.component === 'table'
                          ? {
                                entity: receipt.context_entity,
                                format: receipt.format,
                            }
                          : {}),
                  },
              }
            : {}),
        ...(idColumn !== null ? { selection: { idColumn, actions } } : {}),
        title: renderedTableTitle(receipt.component, receipt.title),
        workspacePath,
        format: receipt.format,
        rowCount: receipt.row_count,
        columns: receipt.columns,
        columnsTruncated: receipt.columns_truncated,
        sourceEventId,
    };
}

type AgencyRulesReceipt = Exclude<
    ReturnType<typeof parseAgencyRulesReceipt>,
    null
>['receipt'];

export function buildAgencyRulesPresentation(
    receipt: AgencyRulesReceipt,
    workspacePath: string,
    sourceEventId: string,
): AgencyRulesPresentation {
    return {
        kind: 'agency_rules',
        workspacePath,
        rowCount: receipt.row_count,
        sourceEventId,
    };
}

const nextStepsOutputSchema = z
    .object({
        shown: z.number().int().min(1).max(3),
        actions: z
            .array(
                z
                    .object({
                        label: z.string().min(1).max(48),
                        prompt: z.string().min(1).max(500),
                    })
                    .strip(),
            )
            .min(1)
            .max(3),
    })
    .strip();

const toolOutputPayloadSchema = z
    .object({
        call_id: z.string().min(1),
        tool_name: z.string().min(1).optional(),
        tool_status: z.string().min(1),
        output_json: z.unknown().optional(),
        output_text: z.string().optional(),
        customer_narration_past: z
            .string()
            .min(1)
            .max(120)
            .refine((value) => value.trim().length > 0)
            .optional(),
    })
    .strip()
    .transform((input) => {
        const {
            tool_name: toolName,
            output_json: outputJson,
            output_text: outputText,
            ...safe
        } = input;
        const questionAnswer = questionAnswerOutputSchema.safeParse(outputJson);
        const assignment =
            toolName === 'assign_caregiver'
                ? assignmentOutputSchema.safeParse(outputJson)
                : null;
        const note =
            toolName === 'save_note'
                ? noteOutputSchema.safeParse(outputJson)
                : null;
        const noteResult =
            note === null
                ? null
                : note.success
                  ? {
                        status: note.data.results.every(
                            (result) => result.saved,
                        )
                            ? ('saved' as const)
                            : note.data.results.some((result) => result.saved)
                              ? ('partial' as const)
                              : ('not_saved' as const),
                        details: note.data.results.flatMap((result) =>
                            result.detail ? [result.detail] : [],
                        ),
                    }
                  : {
                        status: 'not_saved' as const,
                        details: ['Could not confirm that the note was saved.'],
                    };
        // The agent reports what reached the EHR; the card never repeats it.
        const assignmentNotices: ReadonlyArray<string> = [];
        const nextSteps =
            toolName === NEXT_STEPS_TOOL_NAME
                ? nextStepsOutputSchema.safeParse(outputJson)
                : null;
        const attachment =
            toolName === 'send_chat_attachment'
                ? chatAttachmentOutputSchema.safeParse(outputJson)
                : null;
        const communicationSchema =
            toolName === SEND_SMS_TOOL_NAME
                ? sendSmsOutputSchema
                : toolName === PLACE_VOICE_CALL_TOOL_NAME
                  ? placeVoiceCallOutputSchema
                  : null;
        let communication =
            communicationSchema === null
                ? null
                : communicationSchema.safeParse(outputJson);
        if (
            communicationSchema !== null &&
            communication !== null &&
            !communication.success
        ) {
            communication = communicationSchema.safeParse(
                parseJsonOutputText(outputText),
            );
        }
        const communicationOutcome =
            communication?.success !== true
                ? null
                : communication.data.status === 'sent' ||
                    communication.data.status === 'calling'
                  ? ('succeeded' as const)
                  : communication.data.status === 'partial'
                    ? ('partial' as const)
                    : communication.data.status === 'interrupted'
                      ? ('interrupted' as const)
                      : ('no_contact' as const);
        const executionStatus =
            typeof outputJson === 'object' && outputJson !== null
                ? (outputJson as { execution_status?: unknown })
                      .execution_status
                : undefined;
        return {
            ...safe,
            tool_status:
                executionStatus === 'failed' ? 'failed' : safe.tool_status,
            assignmentResult:
                assignment === null
                    ? null
                    : assignment.success
                      ? {
                            status: assignment.data.status,
                            ...(assignmentNotices.length > 0
                                ? { notices: assignmentNotices }
                                : {}),
                            shifts: assignment.data.results.map((shift) => ({
                                id: shift.shift_id,
                                assigned: shift.status === 'assigned',
                            })),
                        }
                      : { status: 'rejected' as const, shifts: [] },
            writeReceipt: parseWriteReceipt(toolName, outputJson),
            outreachReceipt: parseOutreachReceipt(toolName, outputJson),
            recommendationsReceipt: parseRecommendationsReceipt(
                toolName,
                outputJson,
            ),
            attachment: attachment?.success
                ? attachment.data.chat_attachment
                : null,
            tableArtifact: parseRenderedRowsReceipt(toolName ?? '', outputJson),
            agencyRulesArtifact: parseAgencyRulesReceipt(
                toolName ?? '',
                outputJson,
            ),
            questionAnswer: questionAnswer.success
                ? questionAnswer.data.answer
                : null,
            communicationOutcome,
            noteResult,
            nextSteps:
                nextSteps?.success === true ? nextSteps.data.actions : null,
        };
    });

export const toolOutputDefinitions = {
    tool_output: defineCompoundMember('tool_output', {
        disposition: 'compound_member',
        schema: toolOutputPayloadSchema,
        malformed: { kind: 'skip' },
        evidence: null,
        issues(payload) {
            return payload.customer_narration_past === undefined
                ? [{ reason: 'missing_customer_narration' }]
                : [];
        },
        slotMember: {
            slot: 'tool_result',
            terminal: true,
            keys(payload) {
                return [{ kind: 'call_id', value: payload.call_id }];
            },
            fragment(payload, occurrence) {
                if (!TERMINAL_SUCCESS_STATUSES.has(payload.tool_status)) {
                    return {
                        kind: 'tool_failed',
                        pastNarration:
                            payload.customer_narration_past ??
                            'Could not complete that part of the request',
                    };
                }
                const result: ToolResultFragment = {
                    kind: 'tool_succeeded',
                    ...(payload.noteResult === null
                        ? {}
                        : { noteResult: payload.noteResult }),
                    ...(payload.assignmentResult === null
                        ? {}
                        : { assignmentResult: payload.assignmentResult }),
                    pastNarration:
                        payload.customer_narration_past ??
                        'Completed the request',
                    ...(payload.questionAnswer === null
                        ? {}
                        : { questionAnswer: payload.questionAnswer }),
                    ...(payload.communicationOutcome === null
                        ? {}
                        : {
                              communicationOutcome:
                                  payload.communicationOutcome,
                          }),
                };
                if (payload.outreachReceipt !== null) {
                    return { ...result, presentation: payload.outreachReceipt };
                }
                if (payload.recommendationsReceipt !== null) {
                    return {
                        ...result,
                        presentation: payload.recommendationsReceipt,
                    };
                }
                if (payload.nextSteps !== null) {
                    return {
                        ...result,
                        presentation: {
                            kind: 'next_steps',
                            actions: payload.nextSteps,
                        },
                    };
                }
                if (payload.writeReceipt !== null) {
                    return { ...result, presentation: payload.writeReceipt };
                }
                if (payload.attachment !== null) {
                    const receipt = payload.attachment;
                    return {
                        ...result,
                        presentation: {
                            kind: 'chat-attachment',
                            version: 1,
                            attachmentId: receipt.attachment_id,
                            filepath: receipt.filepath,
                            filename: receipt.filename,
                            contentType: receipt.content_type,
                            disposition: receipt.disposition,
                            sizeBytes: receipt.size_bytes,
                        },
                    };
                }
                if (
                    payload.agencyRulesArtifact !== null &&
                    occurrence.committed !== null
                ) {
                    return {
                        ...result,
                        presentation: buildAgencyRulesPresentation(
                            payload.agencyRulesArtifact.receipt,
                            payload.agencyRulesArtifact.workspacePath,
                            occurrence.committed.id,
                        ),
                    };
                }
                if (
                    payload.tableArtifact === null ||
                    occurrence.committed === null
                ) {
                    return result;
                }
                return {
                    ...result,
                    presentation: buildRenderedTablePresentation(
                        payload.tableArtifact.receipt,
                        payload.tableArtifact.workspacePath,
                        occurrence.committed.id,
                    ),
                };
            },
        },
    }),
} as const;

export const toolOutputContractFixtures = {
    valid: {
        call_id: 'call-1',
        tool_name: 'lookup',
        tool_status: 'completed',
        customer_narration_past: 'Checked agency records',
    },
    malformed: {
        call_id: 7,
        tool_name: 'lookup',
        tool_status: 'completed',
    },
} as const;

// The registry's one-per-event-type contract fixtures cover the member's
// own schema; these cover the suggest_next_steps output branch.
export const nextStepsToolOutputContractFixtures = {
    valid: {
        call_id: 'call-next-steps',
        tool_name: 'suggest_next_steps',
        tool_status: 'completed',
        customer_narration_past: 'Suggested next steps',
        output_json: {
            shown: 2,
            actions: [
                { label: 'Start outreach', prompt: 'Start an outreach' },
                { label: 'Show conflicts', prompt: 'Show the conflicts' },
            ],
        },
    },
    malformed: {
        call_id: 'call-next-steps',
        tool_name: 'suggest_next_steps',
        tool_status: 'completed',
        customer_narration_past: 'Suggested next steps',
        output_json: { shown: 1, actions: [{ label: '', prompt: '' }] },
    },
} as const;
